import { useMemo } from 'react';
import { formatDistanceToNow } from 'date-fns';

/**
 * Normalises any timestamp shape into a JavaScript Date (or null on failure).
 * Handles: JS Date, Firestore Timestamp (.toDate()), ISO string, Unix number.
 */
function toDate(timestamp) {
  if (!timestamp) return null;
  try {
    if (timestamp instanceof Date) return timestamp;
    if (typeof timestamp.toDate === 'function') return timestamp.toDate();
    const d = new Date(timestamp);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

/**
 * Pure (non-React) function that converts any timestamp into a human-readable
 * relative string.
 *
 * Rules:
 *   < 30 s   → "Just now"
 *   < 7 days → date-fns formatDistanceToNow  (e.g. "5 minutes ago")
 *   ≥ 7 days → localised date string         (e.g. "Dec 15, 2024")
 *
 * Returns an empty string when the timestamp is missing or unparseable.
 *
 * Use this in:
 *   - .map() callbacks (calling hooks inside .map() is invalid)
 *   - Utility functions outside React components
 *   - anywhere hooks cannot be called
 *
 * @param {Date|import('firebase/firestore').Timestamp|string|number|null|undefined} timestamp
 * @returns {string}
 */
export function formatRelativeTime(timestamp) {
  const date = toDate(timestamp);
  if (!date) return '';

  try {
    const diffMs = Date.now() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);

    if (diffSeconds < 30) {
      return 'Just now';
    }

    if (diffSeconds < 604_800) {
      // < 7 days — human-readable distance ("5 minutes ago", "3 days ago", …)
      return formatDistanceToNow(date, { addSuffix: true });
    }

    // ≥ 7 days — show the calendar date
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

/**
 * React hook version of formatRelativeTime.
 *
 * Wraps the pure function in useMemo so the formatted string is only
 * recomputed when `timestamp` changes.  Use this inside function components
 * where the timestamp belongs to a single item (e.g. a standalone comment
 * card) rather than inside a .map().
 *
 * @param {Date|import('firebase/firestore').Timestamp|string|number|null|undefined} timestamp
 * @returns {string}
 */
export function useRelativeTime(timestamp) {
  return useMemo(() => formatRelativeTime(timestamp), [timestamp]);
}

export default useRelativeTime;
